<template>
    <div class="careai__themed-table">
        <div class="careai__tools--row" v-if="Array.isArray(searchesEnabled) && searchesEnabled.length">
            <div class="careai__searchs--row">
                <div v-if="Array.isArray(tenantOptions) && tenantOptions.length" class="careai__searches--row__item">
                    <strong>Tenant</strong>
                    <b-input-group>
                        <b-form-select v-model="tenantSelected" @input="onTenantChange" :options="tenantOptions" />
                    </b-input-group>
                </div>
                <div v-for="(item, index) in searchesEnabled" class="careai__searches--row__item" :key="`${index}-search`">
                    <strong>{{ item.label }}</strong>
                    <b-input-group>
                        <b-icon class="careai__searches--row-badge" icon="search">🔎</b-icon>
                        <b-input class="themed-table-input" :id="item.key" v-model="searchValues[item.key]" @input="() => onSearchInput(field.label)" />
                    </b-input-group>
                </div>
            </div>
            <div class="careai__tools--row-actions">
                <b-button v-if="addItem.label && addItem.element" class="careai__tools--row-button" @click="showAddItem = !showAddItem">{{ showAddItem ? 'x' : '+' }}</b-button>
            </div>
        </div>      
        <div class="themed-table__toolbar">
            <div v-if="refreshEnabled" @click="onRefresh" class="refresh-wrapper">
                <transition name="fade" mode="out-in">
                    <svg
                        v-if="!refreshing && !debounceTimeout"
                        key="icon"
                        class="refresh-icon"
                        role="img"
                        aria-label="Refresh"
                        viewBox="0 0 25 25"
                        xmlns="http://www.w3.org/2000/svg"
                        focusable="false"
                    >
                        <path fill="none" stroke="currentColor" stroke-width="2" d="M21 12a9 9 0 1 0-3 6.7" />
                        <polyline fill="none" stroke="currentColor" stroke-width="2" points="21 3 21 9 15 9" />
                    </svg>
                    <b-spinner
                        v-else
                        key="spinner"
                        small
                        variant="primary"
                    ></b-spinner>
                </transition>
            </div>
            <b-spinner v-else-if="debounceTimeout" small label="Debouncing" class="debounce-spinner"></b-spinner>

            <div
                v-if="downloadEnabled"
                @click="onDownload"
                class="download-wrapper"
                :class="{ 'download-wrapper--disabled': downloading }"
                v-b-tooltip.hover.top="downloading ? 'Preparing CSV…' : downloadTooltip"
            >
                <transition name="fade" mode="out-in">
                    <svg
                        v-if="!downloading"
                        key="icon"
                        class="download-icon"
                        role="img"
                        aria-label="Download CSV"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                        focusable="false"
                    >
                        <path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M12 3v12m0 0l-4.5-4.5M12 15l4.5-4.5" />
                        <path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" d="M4 19h16" />
                    </svg>
                    <b-spinner
                        v-else
                        key="spinner"
                        small
                        variant="primary"
                    ></b-spinner>
                </transition>
            </div>

           <b-form-select
            v-if="Array.isArray(toolbarDropdown) && toolbarDropdown.length"
            class="themed-table__toolbar-dropdown"
            v-model="selectedToolbarDropdown"
            :options="toolbarDropdown"
            @change="onToolbarDropdownChange"
            />
            
            <div v-if="addItem.label && addItem.element"  @click="handleAddItemClick" class="careai__tools--row-button-span">
                Add {{ addItem.label }}             
                <b-button 
                    class="careai__tools--row-button">{{ showAddItem ? 'x' : '+' }}</b-button>
            </div>

            <div v-if="showAddItem" class="themed-table__add-item" :class="{ 'themed-table__add-item--create-jobs': addItem.element && addItem.element.name === 'CreateJobs' }">
                <component @exit="showAddItem = false" :is="addItem.element"></component>
            </div>
            <slot name="toolbar" />
        </div>
        <div class="themed-table__body">
            <b-table class="themed-table__table"
                :stacked="'lg'"
                :no-local-sorting="true"
                :borderless="true"
                :small="true"
                :tbody-tr-class="'careai__table--row'"
                :thead-tr-class="'careai__table--header'"
                :items="pagedData"
                :fields="fields"
                @row-clicked="onRowClick"
                @sort-changed="() => {}"
                sort-icon-left
                v-bind="{ responsive: false, fixed: true, ...tableProps }"
            >
                <template #head()="{ field }">
                    <div
                        class="header-interaction"
                        @click.stop.prevent="() => null"
                    >
                        <a class="sort-container" v-if="sortingEnabled && field.sortable" @click.stop="() => toggleSorting(field)">
                            <span v-if="sorting.type !== (field.sortKey || field.key)">⇵</span>
                            <span v-if="sorting.type === (field.sortKey || field.key) && sorting.direction === 'desc'">↓</span>
                            <span v-if="sorting.type === (field.sortKey || field.key) && sorting.direction === 'asc'">↑</span>
                        </a>
                        {{ field.label }}
                        <div v-if="field.searchable && field.label" class="header-search-wrap">
                            <span @click.stop="() => {
                                if (isSearching.includes(field.label)) isSearching = [];
                                else {
                                    isSearching = []
                                    isSearching.push(field.label)
                                };
                                if (field.searchable === 'pocTree' && field.treeData && field.treeData.length) {
                                    field.treeData.forEach((fac) => {
                                        fac.expanded = false;
                                        fac.selected = field.filterActive ? fac.selected : false;
                                        if (Array.isArray(fac.children)) {
                                            fac.children.forEach((unit) => {
                                                unit.expanded = false;
                                                unit.selected = field.filterActive ? unit.selected : false;
                                                if (Array.isArray(unit.children)) {
                                                    unit.children.forEach((area) => {
                                                        area.expanded = false;
                                                        area.selected = field.filterActive ? area.selected : false;
                                                    })
                                                }
                                            })
                                        }
                                    });
                                    field._raw.treeData = field.treeData;
                                    locationTreeSearchValue = '';
                                    $emit('locationTreeSearch', '');
                                }
                            }" 
                            v-bind:class="{
                                'search-elipsis': true,  
                                'search-elipsis-open': searchingList.includes(field.label),
                                'search-elipsis-active': (searchValues[field.searchable] && field.searchType !== 'options')
                                                        || (searchValues[field.searchable] && searchValues[field.searchable].length && field.searchType === 'options')
                                                        || (field.searchType === 'dateRange' && (searchValues['createdStartDate'] || searchValues['createdEndDate']))
                                                        || field.filterActive === true
                            }">
                                ⋮
                            </span>
                            <div v-click-outside="() => {
                                isSearching = [];
                                if (field.searchable === 'pocTree' && field.treeData && field.treeData.length) {
                                    field.treeData.forEach((fac) => {
                                        fac.expanded = false;
                                        fac.selected = field.filterActive ? fac.selected : false;
                                        if (Array.isArray(fac.children)) {
                                            fac.children.forEach((unit) => {
                                                unit.expanded = false;
                                                unit.selected = field.filterActive ? unit.selected : false;
                                                if (Array.isArray(unit.children)) {
                                                    unit.children.forEach((area) => {
                                                        area.expanded = false;
                                                        area.selected = field.filterActive ? area.selected : false;
                                                    })
                                                }
                                            })
                                        }
                                    });
                                    field._raw.treeData = field.treeData;
                                    locationTreeSearchValue = '';
                                    $emit('locationTreeSearch', '');
                                }
                            }" @click.stop v-if="isSearching.includes(field.label)" :class="{
                                'careai__searches--search__item': true,
                                'careai__tools--header-item': true,
                                'careai__searches--search__item-right': (fields.length - 2) <= field.i,
                                'careai__searches--search__item-wide': field.searchable === 'pocTree'
                            }">
                                <template v-if="field.searchable !== 'pocTree'">
                                    <span class="search-label-row" v-if="field.searchLabel">
                                        <span>{{field.searchLabel}}</span>
    
                                        <a class="clear-button" v-if="field.searchable !== 'pocTree'" @click="() => {
                                            if (field.searchType === 'dateRange') {
                                                searchValues['createdStartDate'] = null;
                                                searchValues['createdEndDate'] = null;
                                            } else if (field.key === 'tenant') {
                                                tenantSelected = '';
                                                searchValues[field.searchable] = null;
                                                onTenantChange();
                                                return;
                                            } else {
                                                searchValues[field.searchable] = null;
                                            }
    
                                            onSearchInput(field.label);
                                        }">Clear</a>
                                    </span>
                                    <b-input-group class="search-input-group">
                                        <template v-if="!field.searchItems && (!field.searchType || field.searchType === 'text')">
                                            <b-input :id="field.searchable" v-model="searchValues[field.searchable]" @input="() => onSearchInput(field.label)" />
                                            <b-icon class="careai__searches--row-badge" icon="search"><span class="search-icon-ghost">🔎</span></b-icon>
                                        </template>
                                        <template v-if="field.searchItems && field.searchType === 'options'">
                                            <multiselect 
                                                label="text" track-by="value"
                                                v-model="searchValues[field.searchable]" :options="field.searchItems.filter((item) => item.value !== null)" :multiple="field.searchMulti"
                                                :close-on-select="!field.searchMulti"
                                                @search-change="(slug) => onSearchCallbackInput(field.searchCallback, slug, field)"
                                                @input="() => onSearchInput(field.label)" open-direction="bottom"
                                                :placeholder="field.searchLabel" />
                                        </template>
                                        <template v-if="field.searchType === 'dateRange'">
                                            <b-form-datepicker ref="startDate" :max="new Date()" :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }" 
                                            size="sm" placeholder="Start" v-model="searchValues['createdStartDate']" @input="() => onSearchInput()"></b-form-datepicker>
                                            <span>to</span>
                                            <b-form-datepicker ref="startDate" :min="rangeMin" :max="new Date()" :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }" 
                                            size="sm" placeholder="End" v-model="searchValues['createdEndDate']" @input="() => onSearchInput(field.label)"></b-form-datepicker>
                                        </template>
                                    </b-input-group>
                                </template>
                                <template v-if="field.searchable === 'pocTree'">
                                    <themed-location-tree 
                                        @areasChange="(event) => {
                                            isSearching = [];
                                            pocTreeActive = Array.isArray(event) && !!event.length;
                                            $emit('areasChange', event);
                                        }"
                                        @searchChange="(event) => {
                                            locationTreeSearchValue = event;
                                            $emit('locationTreeSearch', event);
                                        }"
                                        :fieldRef="field._raw"
                                        :activeLocationSearch="locationTreeSearchValue"
                                        :treeData="field.treeData">
                                    </themed-location-tree>
                                </template>
                            </div>
                        </div>
                    </div>
                    <!-- Filtering Options Tooltip -->
                    <div
                        v-if="currentFilterKey === field.key"
                        v-click-outside="handleClickOutsideFiltering"
                        class="floating-container"
                        ref="state-filter"
                    >   
                        <small class="filtering-title">Filtering</small>
                        <div v-for="(option, index) in field.filteringOptions" :key="index">
                            <label>
                                <input
                                    type="checkbox"
                                    v-model="option.active"
                                    :value="option"
                                    @change="onFilterChange(field.key)"
                                />
                                {{ option.key }}
                            </label>
                        </div>
                    </div>
    
                </template>
    
                <!-- Dynamic rendering of cell content -->
                <template #cell()="{ item, field, index }">
                   <div v-b-toggle="`themedTable-${field.expandableKey}-${index}`" @click="(event) => { $emit('rowClick', item, event);}" class="cell-clickable">
                        <div v-if="field.renderAsHtml">
                            <span v-b-tooltip.hover.top="item.taskMessage"  v-if="field.key=== 'taskStatus' && item.taskMessage" class="info-icon-wrapper">
                                <font-awesome-icon class="info-icon-svg" icon="info-circle" :color="item.infoIconColor || 'rgba(0, 181, 218, 0.9)'" />
                            </span>
                            <span v-html="item[field.key]"></span>
                        </div>
                        <div v-else-if="field.renderAsIcons" class="icons-row">
                            <font-awesome-icon 
                                class="table-icon"
                                v-b-tooltip.hover :title="icon.tooltip"
                                :color="'rgba(0, 181, 218, 0.9)'" 
                                v-for="(icon, i) in item.icons" 
                                :key="field.key + i + 'icon'"
                                :icon="icon.slug" @click="icon.onClick" />
                        </div>
                        <div v-else-if="Array.isArray(item[field.key])">
                            <div v-for="(row, ri) in item[field.key]" :key="field.key + 'row' + ri">
                                {{ row }}
                            </div>
                        </div>
                        <div v-else-if="field.key === 'action'">
                        <b-button
                            size="sm"
                            variant="link"
                            @click.stop="$emit('actionClick', item)"
                        >
                            {{ item.action }}
                        </b-button>
                        </div>
                        <div class="themed-table__break" v-else>{{ item[field.key] ? item[field.key] : field.default ? field.default : '' }}</div>
                    </div>
                    <b-collapse 
                        v-click-outside="(ref) => { $refs[ref] && $refs[ref].show ? handleClickOutsideInfo(ref) : null }" 
                        :ref="`collapses-info-${index}`" 
                        class="floating-container" 
                        v-if="field.expandableKey && item[field.expandableKey]" 
                        :id="`themedTable-${field.expandableKey}-${index}`"
                        @shown="onCollapseShown(index)" 
                        @hidden="onCollapseHidden(index)">
                        <div v-html="item[field.expandableKey]"></div>
                    </b-collapse>
                </template>
            </b-table>
        </div>
        <div class="sticky-footer">
            <div class="version">{{ version }}</div>
            <b-pagination v-if="paginationEnabled" 
                :total-rows="pagination.total"
                :per-page="pagination.perPage"
                v-model="currentPage"
                @change="onPaginationChange"
            />
        </div>

    </div>
</template>

<script>
import Vue from 'vue';
import ThemedLocationTree from './ThemedLocationTree.vue';
Vue.component('themed-location-tree', ThemedLocationTree);
export default {
    name: "ThemedTable",
    components: {},
    props: {
        version: {
            type: String,
            default: '0.0.0'
        },
        toolbarDropdown: {
            type: Array,
            default: () => ([]),
            required: false,
        },
        toolbarDropdownValue: {
            type: [String, Object],
            default: null,
            required: false,
        },
        addItem: {
            type: Object,
            default: () => ({
                label: null, 
                element: null, 
            }),
            required: false
        },
        data: {
            type: Array,
            required: true,
        },
        headers: {
            type: Array,
            required: true,
        },
        sorting: {
            type: Object,
            default: () => ({
                type: null,    // key for sorting (e.g., "name")
                direction: "asc", // default sort direction
            }),
        },
        sortingEnabled: {
            type: Boolean,
            required: true,
        },
        localSorting: {
            type: Boolean,
            required: false,
        },
        tenantSelected: {
            type: Object,
            required: false,
        },
        tenantOptions: {
            type: Array,
            required: false,
        },
        syncSearchValues: {
            type: Object,
            required: false
        },
        baseDelay: {
            type: Number,
            default: 3000, // Default delay of 3 seconds
        },
        // SEARCHES ENABLED IS TO BE DEPRECATED, headerObjects now supported searchable property which 
        // will render in careai theme table header dropdowns
        searchesEnabled: {
            type: Array,
            default: function () {
                return [];
            },
        },
        pagination: {
            type: Object,
            default: null,
        },
        paginationEnabled: {
            type: Boolean,
            required: true,
        },
        filteringEnabled: {
            type: Boolean,
            required: true,
        },
        locationTreeSearchValue: {
            type: String,
            default: '',
        },
        tableProps: {
            type: Object,
            default: () => ({}),
        },
        refreshEnabled: {
            type: Boolean,
            default: false,
        },
        downloadEnabled: {
            type: Boolean,
            default: false,
        },
        // Caller-controlled: the download can involve async work of unpredictable length
        // (e.g. paging through an API), so unlike `refreshing` this isn't guessed with a timeout.
        downloading: {
            type: Boolean,
            default: false,
        },
        downloadTooltip: {
            type: String,
            default: 'Download CSV',
        },
    },
    data() {
        return {
            console: console,
            selectedToolbarDropdown: null,
            ThemedLocationTree,
            key: (Math.random() + 1).toString(36).substring(7),
            isCollapseActive: [],
            currentPage: 1,
            longPressTimeout: null,
            filteringVisible: false,
            currentFilterKey: null,
            isSearching: [],
            debounceTimeout: null,
            showAddItem: false,
            searchValues: {
                createdStartDate: null,
            },
            pocTreeActive: false,
            refreshing: false,
            isClickPending: false,
        };
    },
    watch: {
        pagination: {
            deep: true,
            handler(value) {
                if (value && value.page > 0 && this.currentPage !== value.page) {
                    this.currentPage = value.page;
                }
            },
        },
        searchesEnabled: {
            immediate: true,
            handler(newSearches) {
                // Initialize search values if searchesEnabled changes
                newSearches.forEach((item) => {
                    if (!(item.key in this.searchValues)) {
                        this.$set(this.searchValues, item.key, "");
                    }
                });
            },
        },
        tenantSelected() {
            this.searchValues.scope = this.tenantSelected;
        },
        syncSearchValues() {
            if (this.syncSearchValues) {
                Object.keys(this.syncSearchValues).forEach((key) => {
                    if (!this.syncSearchValues[key] || !this.syncSearchValues[key].length)  delete this.syncSearchValues[key];
                })
                this.searchValues = { ...this.syncSearchValues, scope: this.tenantSelected }
            }
        },
          toolbarDropdownValue: {
            immediate: true,
            handler(value) {
            this.selectedToolbarDropdown = value;
        }
    }
    },
    computed: {
        rangeMax() {
            const date = this.searchValues['createdEndDate'] ? new Date(this.searchValues['createdEndDate']) : null;
            if (date) {
                date.setDate(date.getDate() + 1)
            }
            return date;
        },
        rangeMin() {
            const date = this.searchValues['createdStartDate'] ? new Date(this.searchValues['createdStartDate']) : null;
            if (date) {
                date.setDate(date.getDate() + 1)
            }
            return date;
        },
        fields() {
            return this.headers
                .filter((header) => header.active !== false)
                .map((header, i) => ({
                    i,
                    key: header.key,
                    label: header.label,
                    expandableKey: header.expandableKey,
                    renderAsHtml: header.renderAsHtml,
                    renderAsIcons: header.renderAsIcons,
                    sortable: header.sortable && this.sortingEnabled && !this.filteringVisible,
                    sortKey: header.sortKey,
                    searchable: header.searchable,
                    searchLabel: header.searchLabel,
                    searchItems: header.searchItems,
                    searchMulti: header.searchMulti,
                    searchType: header.searchType ? header.searchType : 'text',
                    searchCallback: header.searchCallback ? header.searchCallback : () => null,
                    treeData: header.treeData ? header.treeData : [],
                    filteringOptions: header.filteringOptions || [],
                    filterActive: header.filterActive,
                    _raw: header,
                }));
        },
        filteredData() {
            return this.data;
        },
        pagedData() {
            return this.filteredData;
        },
        searchingList() {
            return this.isSearching;
        }
    },
    mounted() {
        Vue.directive('click-outside', {
            bind(el, binding, vnode) {
                var vm = vnode.context;
                var callback = binding.value;

                el.clickOutsideEvent = function (event) {
                    if (!(el == event.target || el.contains(event.target))) {
                        return callback.call(vm, event);
                    }
                };
                document.body.addEventListener('click', el.clickOutsideEvent);
            },
            unbind(el) {
                document.body.removeEventListener('click', el.clickOutsideEvent);
            }
        });
        if (this.tenantSelected) {
            this.searchValues.scope = this.tenantSelected;
        }
    },
    methods: {
       onToolbarDropdownChange(value) {
            const selectedValue = value && value.target ? value.target.value : value;  
            this.$emit('toolbar-dropdown-change', selectedValue);
        },
        onTenantChange() {
            this.stopDebounce();
            this.debounceTimeout = setTimeout(() => {
                this.$emit("tenantChange", this.tenantSelected);
                this.stopDebounce();
            }, this.baseDelay);
        },
        onSearchInput(fieldLabel) {
            this.stopDebounce();
            this.debounceTimeout = setTimeout(() => {
                this.isSearching = this.isSearching.filter((label) => label !== fieldLabel);
                this.$emit("searchChange", this.searchValues);
                this.stopDebounce();
            }, this.baseDelay);
        },
        onSearchCallbackInput(callback, slug, field) {
            if (field.currentSearch !== slug) {
                field.currentSearch = slug;
                this.debounceTimeout = setTimeout(() => {
                    if (callback) { callback(slug) }
                    this.stopDebounce();
                }, this.baseDelay)            }
        },
        onFilterInput(field) {
            this.console.log(field);
        },
        toggleSorting(field) {
            if (!this.sortingEnabled || !field.sortable) {
                this.$emit("sortingChange", 'delete');
                return;
            }
            const key = field.sortKey || field.key;
            if (this.sorting.type !== key) this.sorting.direction = 'desc';
            if (this.sorting.type === key) this.sorting.direction = this.sorting.direction === 'asc' ? 'desc' : 'asc';
            this.sorting.type = key;
            this.sorting = {...this.sorting};
            this.$emit("sortingChange", { type: this.sorting.type, direction: this.sorting.direction });
        },
        onRowClick(item) {
            this.$emit("rowClick", item);
        },
        onPaginationChange(newPage) {
            this.currentPage = newPage;
            if (this.pagination) {
                this.$emit("paginationChange", { page: newPage });
            }
        },
        closeFiltering() {
            this.filteringVisible = false;
            this.currentFilterKey = null;
        },
        onFilterChange(key) {
            const filter = this.headers.find(header => header.key === key)
            this.$emit("filterChange", filter);
        },
        handleClickOutsideFiltering() {
            this.closeFiltering();
        },
        onCollapseShown(index) {
            this.$set(this.isCollapseActive, index, true);
        },
        onCollapseHidden(index) {
            this.$set(this.isCollapseActive, index, false); 
        },
        handleClickOutsideInfo(ref) {
            this.$refs[ref].toggle();
        },
        stopDebounce() {
            if (this.debounceTimeout) {
                clearTimeout(this.debounceTimeout);
            }
            this.debounceTimeout = null;
        },
        onRefresh() {
            if (this.refreshing) return;
            this.refreshing = true;
            this.$emit('refresh');
            setTimeout(() => { this.refreshing = false; }, 1000);
        },
        onDownload() {
            if (this.downloading) return;
            this.$emit('download');
        },
        handleAddItemClick() {
            if (this.isClickPending) return; // Prevent multiple clicks
    
            this.isClickPending = true;
            this.showAddItem = !this.showAddItem;
            
            // Reset after a short delay (e.g., 300ms)
            setTimeout(() => {
            this.isClickPending = false;
            }, 300);
        }
    
    },
};
</script>

<style lang="scss">

    .sticky-footer {
        display: flex;
        position: fixed;
        align-items: center;
        bottom: 0;
        left: 0;
        width: 100%;
        background-color: #eff1f3;
        padding: 10px;
        height: 60px;
        z-index: 1000; /* Ensures it stays above other content */

        .version {
            font-size: 14px;
            color: #787878;
            font-weight:500;
        }
    }   
    
    .refresh-wrapper {
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding-right: 5px;
    }

    .refresh-icon {
        font-size: 0.9rem;
        color: #1dd8f6;
        display: inline-block;
        width: 1rem;
        height: 1rem;
        vertical-align: middle;
        fill: currentColor;
        stroke: currentColor;
    }

    .download-wrapper {
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding-right: 5px;
    }

    .download-wrapper--disabled {
        cursor: wait;
        pointer-events: none;
    }

    .download-icon {
        font-size: 0.9rem;
        color: #1dd8f6;
        display: inline-block;
        width: 1rem;
        height: 1rem;
        vertical-align: middle;
        fill: none;
        stroke: currentColor;
    }

    .careai__tools--row, .careai__tools--header-item {
        font-size: 0.75em;
        padding: 15px;
        border-radius: 5px;
        display: flex;
        gap: 15px;
        position: relative;
        justify-content: space-between;
        align-items: flex-end;
        margin-bottom: 15px;

        input.themed-table-input {
            height: 40px;
            padding: 0 15px;
        }

        select.themed-table-input {
            height: 40px;
            padding: 0 15px;
        }

        .multiselect__tags {
            display: flex;
            flex-direction: column;
            padding: 10px 40px 0 8px;

            .multiselect__tags-wrap {
                display: flex;
                flex-wrap: wrap;
            }

            .multiselect__tag {
                background: #1dd8f6;

                .multiselect__tag-icon {
                    display: flex;
                    justify-content: center;
                    align-items: center;
            
                    &:hover {
                        background-color: #1dd8f6;;
                    }
                }
            }
        }

        .multiselect__option {
            text-overflow: initial;
            white-space: wrap;
        }

        .careai__searches--row-badge {
            display: flex;
            align-items: center;
            justify-content: center;
            background: #fff;
            border-top-right-radius: 5px;
            border-bottom-right-radius: 5px;
            padding: 2px;
            border: 1px solid #e3e9ef;
            border-left-width: 0;
            padding-right: 10px;
            position: relative;
            height: 40px;
            right: 2px;
        }

        .careai__tools--row-button {
            height: 20px;
            width: 20px;
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 0;
            transition: all 500ms;
            background-color: #effefe;
            border: 1px solid rgba(0,181,218,.4);
            color: #c8c8c8;
            padding-bottom: 2px;
            border-radius: 50%;
            font-size: 12px;

            &:hover {
                background-color: #1dd8f6;
                color: white;
            }
        }
    }

    .careai__tools--row-button-span {
        display: flex;
        color: #606060;
        gap: 5px;
        font-size: 14px;
        align-items: center;
        transition: all 250ms;
        cursor: pointer;
        position: relative;
        z-index: 2;

        &:hover {
            color: #1dd8f6;

            .careai__tools--row-button {
                background-color: #1dd8f6;
                color: white;
            }
        }

        .careai__tools--row-button {
            height: 20px;
            width: 20px;
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 0;
            transition: all 500ms;
            background-color: #effefe;
            border: 1px solid rgba(0,181,218,.4);
            color: #c8c8c8;
            padding-bottom: 2px;
            border-radius: 50%;

            &:hover {
                background-color: #1dd8f6;
                color: white;
            }
        }
    }

    .careai__searchs--row {
        font-size: 0.75em;
        border-radius: 5px;
        display: flex;
        gap: 15px;
        position: relative;
        align-items: flex-end;
    }

    .careai__searches--row__item {
        font-size: 1em;
        cursor: pointer;
        display: flex;
        flex-direction: column;
    }
    .careai__searches--search__item {
        position: absolute;
        min-width: 250px;
        max-width: 450px;
        left: 0;
        top: 25px;
        background-color: white;
        padding: 0px;
        filter: drop-shadow(5px 5px 6px #d6d6d6);
        border-radius: 5px;
        display: flex;
        align-items: flex-start;
        flex-direction: column;

        .form-control {
            border-color: #eff0f1;
            border-right-width: 0;
            height: 40px;
        }

        * {
            font-size: 10px !important;
        }

        svg {
            color: #999;
        }

        .input-group {
            padding: 15px;
            padding-top: 0px;
        }

        .multiselect__select {
            height: 15px;
            top: 15px;
        }

        > span {
            font-size: 10px;
            color: #787878;
            font-size: 10px;
            flex: 2;
            display: flex;
            justify-content: space-between;
            width: 100%;
            padding: 15px 15px 0 15px;
        }

        a.clear-button {
            transition: color 250ms;
            &:hover {
                color: #1dd8f6
            }
        }

        .multiselect__content-wrapper {
            margin-top: 15px;
            border: none;
            border-top: 1px solid #dee2e6;
            overflow-x: hidden;
            position: relative;

            ul {
                margin-top: 15px;
            }

            .multiselect__option--highlight::after {
                display: none;
            }

            .multiselect__option--selected {
                background-color: #f2fdfe;
            }

            .multiselect__option--selected::after {
                display: none;
            }

            .multiselect__option--highlight {
                background-color: #f2fdfe;
                color: #787878;
            }
        }
    }
    .careai__searches--search__item-wide {
        width: 350px;
    }
    .careai__searches--search__item-right {
        left: auto;
        right: 0px;
    }
    .careai__table--row {
        font-size: 0.75em;
        cursor: pointer;
        border-bottom: 1.5px solid #eff0f1;
    }
    .careai__table--row:hover {
        transition: all 250ms;
        background-color: rgba(0, 181, 218, 0.1) !important;
    }
    .careai__table--header {
        background-color: #f7f7f7;
        font-size: 15px;
        border-bottom: 1px solid #dee2e6;
        border-top: 1px solid #dee2e6;
        
        .b-form-datepicker {
            padding: 0;
            display: flex;
            align-items: center;
            border: 1px solid #ced4da;
            border-radius: 5px;
            border-top-right-radius: 5px !important;
            border-bottom-right-radius: 5px !important;
            border-top-left-radius: 5px !important;
            border-bottom-left-radius: 5px !important;

            &.focus {
                box-shadow: none;
            }

            .b-calendar-header {
                display: none;
            }

            .b-calendar-grid-help {
                display: none;
            }

            .text-dark {
                color: #606060;
            }

            .btn-primary:not(:disabled):not(.disabled).active, .btn-primary:not(:disabled):not(.disabled):active, .show>.btn-primary.dropdown-toggle {
                color: #1dd8f6;
            }

            .btn-outline-primary {
                color: #606060;
            }

            .btn-outline-primary.focus, .btn-outline-primary:focus {
                box-shadow: none;
            }

            button {
                padding: 0 5px;
                background: transparent;
                transition: all 250ms;
                font-size: 12px !important;

                &:hover {
                    color: #1dd8f6;
                }
            }

            .form-control {
                text-wrap: nowrap;
                text-overflow: ellipsis;
                overflow: hidden;
            }
        }

        .b-calendar .b-calendar-grid-body .col[data-date] .btn {
            font-size: 14px;
            line-height: 1;
            margin: 3px auto;
            padding: 9px 0;
            background: transparent;
        }

        .search-elipsis {
            margin-left: 5px;
            padding: 5px;
            display: flex;
            justify-content: center;
            align-items: center;
            transition: all 250ms;
            height: 15px;
            width: 15px;
            border-radius: 50%;
            position: relative;

            &:hover {
                background-color: #e2e2e2;
            }
        }

        .search-elipsis-open, .search-elipsis-active {
            height: 16px;
            width: 16px;
            padding: 0px;
            padding-bottom: 1px;
            padding-right: 1px;
            border: 1px solid #1dd8f6;
        }
    }
    .header-interaction {
        cursor: pointer;
        display: flex;
        align-items: center;
        user-select: none;
        font-weight: 400;
        font-size: 14px;
        text-wrap: nowrap;
    }

    .sort-container { 
        position: relative; 
        z-index: 2;
        margin-right: 5px;
    }

    .themed-table__modal {
        label {
            color: #2e2e2e;
        }
    }

    .floating-container {
        position: absolute;
        background-color: white;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        padding: 10px;
        z-index: 100;
        border: 1px solid #ddd;
        max-width: 200px;
        width: auto;
    }

    .floating-container label {
        display: block;
        margin: 5px 0;
    }

    .floating-container input {
        margin-right: 5px;
    }

    .careai__themed-table {
        .pagination {
            margin-left: 38%;
            padding-top: 20px;
        }
        tr {
            td {
                color: #787878;
            }
            th {
                color: #5f5f5f;
                background-image: none !important;

                &:last-of-type {
                    padding-right: 100px !important;
                }
            }
            th, td {
                padding: 20px 5px;
            }
            th:first-of-type, td:first-of-type {
                padding-left: 25px;
            }
            th:last-of-type, td:last-of-type {
                padding-right: 25px;
            }
        }

        .form-control:focus {
            border-color: inherit;
            -webkit-box-shadow: none;
            box-shadow: none;
            border-color: #dee2e6;
        }
            .btn-link {
            color: #f8fafb;
            }

        .themed-table__toolbar {
            display: flex;
            gap: 5px;
            position: absolute;
            right: 15px;
            top: -32px;
            align-items: center;
            justify-content: center;

        .themed-table__refresh-button {
                font-size: 14px !important;
                height: 20px;
                width: 20px;
                line-height: 1;
                padding-bottom: 3px;
            }

            .themed-table__toolbar-dropdown {
                width: 170px;
                height: 30px;
                font-size: 12px;
                // Bootstrap's .custom-select arrow is a data:image/svg+xml background-image,
                // which the deployed CSP (default-src https:, no data:) silently blocks.
                // Restore the native OS select arrow instead so it works under any CSP.
                background-image: none !important;
                -webkit-appearance: menulist !important;
                -moz-appearance: menulist !important;
                appearance: menulist !important;
                padding-right: 0.75rem !important;
            }

            .themed-table__add-item {
                position: absolute;
                min-width: 700px;
                right: 0;
                top: 30px;
                background: #fff;
                border: 1px solid #dee2e6;
                border-radius: 5px;
                filter: drop-shadow(5px 5px 6px #d6d6d6);
                z-index: 3;
                font-size: 12px;
                max-width: 800px;

                .multiselect__placeholder {
                    color: #adadad;
                    display: inline-block;
                    margin-bottom: 10px;
                    padding-top: 2px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
            }
        }

        .careai__tools--header-search-items {
            width: 100%;
            border-top: 1px solid #dee2e6;
            margin-top: 15px;
            padding-top: 15px;

            .careai__tools--header-search-item  {
                padding: 7.5px;
                transition:  all 250ms;
                border-radius: 5px;

                &:hover {
                    background-color: #f2fdfe;
                }
            }

            .careai__tools--header-search-item-selected  {
                background-color: #f2fdfe;
            }
        }

        .themed-table__break {
            max-width: 400px;
            white-space: normal;
            overflow-wrap: break-word;
        }
        
    }

    .tooltip-inner {
        max-width: 400px;  
        padding: 10px 15px; 
    }

</style>

<style lang="scss">
    .careai__tools--row-actions {
        display: flex;
        gap: 5px;
        align-items: center;
    }

    .debounce-spinner {
        border-left-color: #1dd8f6 !important;
        border-top-color: #1dd8f6 !important;
        border-bottom-color: #1dd8f6 !important;
    }

    .themed-table__add-item--create-jobs {
        min-width: 950px !important;
        max-width: 95vw !important;
        left: 50% !important;
        top: 50% !important;
        transform: translate(-50%, -50%) !important;
        position: fixed !important;
        right: auto !important;
        z-index: 1040 !important;
    }

    .themed-table__body {
        height: 75vh;
        overflow-y: auto;
        padding-bottom: 20px;
    }

    .themed-table__table {
        overflow-y: auto;
    }

    .header-search-wrap {
        position: relative;
        z-index: 2;
    }

    .search-label-row {
        display: flex;
        justify-content: space-between;
        width: 100%;
    }

    .search-input-group {
        gap: 2px;
        align-items: center;
    }

    .search-icon-ghost { opacity: 0.5 }

    .filtering-title { font-weight: 800 }

    .cell-clickable { z-index: 10; cursor: pointer }

    .info-icon-wrapper { margin-right: 3px }

    .info-icon-svg { pointer-events: all; width: 15px }

    .icons-row { display: flex; padding: 5px; justify-content: flex-start; align-items: center }

    .table-icon { pointer-events: all; width: 14px }

</style>
